import { FarmSuggestion, LevelCatalog, Modes, farmSuggestion } from "@highmarch/app";
import { icon } from "../iconsKit";
import { btnHtml, fmtCompact } from "../kit";
import { chestIconUrl, gearIconUrl, portraitUrl, resourceIconUrl } from "../portraits";
import { prizeView } from "./prizeFocus";
import { chapterOf } from "../chapters";
import { LevelFacts, levelFacts, levelSubline, starRowHtml } from "./levelFacts";
import { Readiness } from "./readiness";
import { ironStarGlyph } from "../modeGlyphs";
import { features } from "../../features/registry";

/** Enemy faces on the card: the boss (ringed red) or the new enemy first, then the rest in spawn order. */
const MAX_FACES = 3;
/** Numbers under 10,000 keep every digit with a thousands separator; above, the compact form (12.4K). */
const COMPACT_FROM = 10000;
export const fmtCardNumber = (n: number): string => (n < COMPACT_FROM ? n.toLocaleString("en-US") : fmtCompact(n));
/** Art on the farm row's payout chips (CSS px). */
const PAY_ICON_PX = 20;


/** The level's enemy faces (the battle card's slim strip, the campaign panel): `max` of them, boss or new ones first. */
export function facesHtml(f: LevelFacts, max = MAX_FACES): string {
  const ordered = f.isBossLevel ? [f.boss, ...f.enemies.filter((e) => e.index !== f.boss.index)] : [...f.newEnemies, ...f.enemies.filter((e) => !f.newEnemies.includes(e))];
  return ordered.slice(0, max).map((e) => {
    const url = portraitUrl(e.id);
    const boss = f.isBossLevel && e.index === f.boss.index;
    const fresh = f.newEnemies.includes(e) && !boss;
    return `<span class="face${boss ? " boss" : ""}">${url ? `<img decoding="sync" src="${url}" alt="">` : icon("skull", 16)}${fresh ? `<i class="new">NEW</i>` : ""}</span>`;
  }).join("");
}

function chapterRowHtml(catalog: LevelCatalog, level: number): string {
  const ch = chapterOf(catalog.balance, level, catalog.levelCount);
  const count = ch.last - ch.first + 1;
  const cleared = Array.from({ length: count }, (_, i) => ch.first + i).filter((l) => catalog.isCleared(l)).length;
  const pips = Array.from({ length: count }, (_, i) => {
    const l = ch.first + i;
    const cls = l === ch.last ? "boss" : catalog.isCleared(l) ? "on" : l === level ? "cur" : "";
    return `<i class="pip ${cls}${l === ch.last && catalog.isCleared(l) ? " on" : ""}"></i>`;
  }).join("");
  return `<button class="chap">${icon("map", 18)}<span class="cname">Ch ${ch.index + 1} · ${ch.name}</span><span class="pips">${pips}</span><span class="cnt">${cleared}/${count}</span>${icon("chev", 16)}</button>`;
}

/**
 * FARM L{n} (docs/SHOP.md §3.3 K6): when the next level is not Ready, the card offers a replay of a cleared level —
 * the grind's way to gear and resources — beside PLAY, which stays. The level and its lines are SHOP-ECONOMY's
 * `farmSuggestion` (@highmarch/app readiness.ts); `now` names a running event in its hint.
 */
export function farmOffer(catalog: LevelCatalog, r: Readiness, now: number | null = null): FarmSuggestion | null {
  return farmSuggestion(catalog, now, r);
}

function farmHtml(f: FarmSuggestion | null, iron = "", extra = ""): string {
  // The hint (what is short, and the running event's embers: the event chip is on screen) is the button's spoken label.
  if (!f) return iron ? `<div class="farmrow ironrow">${iron}</div>` : "";
  return `<div class="farmrow${iron ? " withiron" : ""}"><button type="button" class="btn blue disp farmbtn" data-level="${f.level}" aria-label="${f.hint}">${f.button}</button>${iron || farmPayHtml(f)}${extra}</div>`;
}

/** What one farm run pays, as art and numbers (the gold, the gear items); the whole line is its spoken label. */
function farmPayHtml(f: FarmSuggestion): string {
  const gear = f.extraGearPercent > 0 ? "1–2" : "1";
  return `<span class="fpay" role="note" aria-label="${f.payout}"><span class="fp"><img decoding="sync" src="${resourceIconUrl("gold")}" alt="" width="${PAY_ICON_PX}" height="${PAY_ICON_PX}">~${fmtCardNumber(f.gold)}</span>`
    + `<span class="fp"><img decoding="sync" src="${gearIconUrl("ammo", "rare")}" alt="" width="${PAY_ICON_PX}" height="${PAY_ICON_PX}">${gear}</span></span>`;
}

/** The glyph on the IRON button (CSS px). */
const IRON_GLYPH_PX = 20;

/**
 * IRON L{n} (M21, docs/MODES.md): a 3-star level's Iron challenge whose try is free today — the highest still without
 * its Iron star, else the highest to win again (Modes.ironSuggestion). Beside FARM when the card has it; its taps open
 * the Iron sheet (features/modes.ts). "" when none is free or the clock is unknown.
 */
export function ironHtml(catalog: LevelCatalog, now: number | null): string {
  if (now === null) return "";
  const m = new Modes(catalog), level = m.ironSuggestion(now);
  if (level === null) return "";
  const fresh = !catalog.modes.hasIronStar(level);
  return `<button type="button" class="btn dark disp ironbtn" data-level="${level}" aria-label="Iron challenge, level ${level}">${ironStarGlyph(IRON_GLYPH_PX)}<span>IRON L${level}</span></button><small class="ironsub">${fresh ? "Iron star" : "Marks"}</small>`;
}

/** Delegated taps for FARM under `root` (the hub re-renders the card's HTML); `play` starts the replay as PLAY does. */
export function bindFarmButton(root: HTMLElement, play: (level: number) => void): () => void {
  const onClick = (e: MouseEvent) => {
    const b = (e.target as HTMLElement | null)?.closest?.<HTMLElement>(".battlecard .farmbtn");
    if (b && Number(b.dataset.level) > 0) play(Number(b.dataset.level));
  };
  root.addEventListener("click", onClick);
  return () => root.removeEventListener("click", onClick);
}

/**
 * The battle card (Lobby concept + Campaign's readiness strip): chapter row → the map, the next level (name, stages,
 * the first-clear chest), the readiness strip, and the kicker ribbon on the level gear becomes required. PLAY sits
 * under it (hubView.ts). The strip only informs: it never blocks PLAY.
 */
export function battleCardHtml(catalog: LevelCatalog, level: number, r: Readiness, now: number | null = null): string {
  const B = catalog.balance;
  if (catalog.highestLevelCleared >= catalog.levelCount) {
    // D10: v1.0's campaign ends at level 60; the card says what comes next and where the endgame is.
    return `<div class="battlecard plate">${chapterRowHtml(catalog, level)}<div class="lvl ch7line"><div class="disc">${icon("trophy", 26)}</div><div class="ltext"><b class="disp">Chapter ${catalog.chapterCount + 1} — coming soon</b><small>Heroic &amp; Trials await in Events</small></div></div>${farmHtml(null, ironHtml(catalog, now))}</div>${continueRowHtml()}`;
  }
  const f = levelFacts(B, level);
  const cleared = catalog.isCleared(level);
  // owner 2026-10-23: no readiness strip ("Under-geared", "No gear needed until…") and no "Gear required" ribbon on
  // the card — the player discovers it in battle
  const kicker = "";
  // A cleared level is a farm run (K3): its chest is the farm chest. The chest shows the best rarity it can roll, on
  // that rarity's colour; a tap opens it in focus with the odds (prizeFocus.ts).
  const farmSug = cleared ? null : farmOffer(catalog, r, now);
  const pv = prizeView(B, level, cleared);
  const prize = `<button type="button" class="prize ri-${pv.topId}" data-level="${level}" aria-label="${cleared ? "Farm chest" : "First-clear chest"}, up to ${pv.topId}: see the odds"><span class="sq"><img decoding="sync" src="${chestIconUrl(pv.topId)}" alt=""></span><span class="tag">${cleared ? "Farm" : "1st clear"}</span></button>`;
  return `<div class="battlecard plate${kicker ? " kicked" : ""}">${kicker}
    ${chapterRowHtml(catalog, level)}
    <div class="lvl"><div class="disc${f.isBossLevel ? " boss" : ""}">${f.isBossLevel ? icon("skull", 24, "#fff") : `<span class="disp">${level}</span>`}</div>
      <div class="ltext"><b class="disp">Level ${level} · ${f.name}</b><small class="${f.isBossLevel ? "bossline" : ""}">${levelSubline(f)}</small>${starRowHtml(B, level, catalog.starsFor(level))}</div>${prize}</div>
    ${farmHtml(farmSug, ironHtml(catalog, now), farmSug ? features.farmRowHtml({ surface: "card", level, cleared, farmLevel: farmSug.level }) : "")}
    ${features.preBattleHtml({ surface: "card", level, cleared, farmLevel: farmSug?.level ?? null })}
  </div>${continueRowHtml()}`;
}

/** A battle put aside (suspend/resume, main.ts): the level, its name and the wave it stopped on, of how many. */
export interface ContinueInfo { readonly level: number; readonly name: string; readonly wave: number; readonly waves: number; readonly mode?: "iron" | "endless" | "daily" | "challenge" | "boss" | "race" | "odyssey" }

/** Set by the game before the hub renders (and cleared on abandon); null hides the CONTINUE row. */
let continueInfo: ContinueInfo | null = null;
export function setContinueInfo(info: ContinueInfo | null): void { continueInfo = info; }

/**
 * CONTINUE · Level N · wave W/T under the card, in PLAY's place (keep.css hides PLAY beside it), with a small
 * "Abandon" beside it. Its taps are handled by `bindContinueRow`, since the hub re-renders the card's HTML.
 */
function continueRowHtml(): string {
  const c = continueInfo;
  if (!c) return "";
  const wave = c.wave <= 0 ? "before wave 1" : c.mode === "endless" ? `wave ${c.wave}` : c.mode === "iron" ? "the Iron wave" : `wave ${c.wave}/${c.waves}`;
  const what = c.mode === "endless" ? "Endless" : c.mode === "iron" ? `Iron · Level ${c.level}` : c.mode === "challenge" ? `Challenge · Level ${c.level}` : c.mode === "daily" || c.mode === "boss" || c.mode === "race" || c.mode === "odyssey" ? c.name : `Level ${c.level} · ${c.name}`;
  return `<div class="controw">${btnHtml("CONTINUE", "gold", { icon: "play", className: "cont accent disp", sub: `${what} · ${wave}` })}<button class="abandon">Abandon</button></div>`;
}

/** Delegated taps for the CONTINUE row under `root` (the game's UI root, which outlives every hub render). */
export function bindContinueRow(root: HTMLElement, h: { onContinue: () => void; onAbandon: () => void }): () => void {
  const onClick = (e: MouseEvent) => {
    const t = (e.target as HTMLElement | null)?.closest?.(".controw button");
    if (!t) return;
    if (t.classList.contains("cont")) h.onContinue();
    else if (t.classList.contains("abandon")) h.onAbandon();
  };
  root.addEventListener("click", onClick);
  return () => root.removeEventListener("click", onClick);
}
